'use client';

import { useId, useState } from 'react';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const nameId = useId();
  const emailId = useId();
  const messageId = useId();
  const companyId = useId();

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'),
          email: data.get('email'),
          message: data.get('message'),
          company: data.get('company'),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Something went wrong. Please try again.');
      }

      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (status === 'success') {
    return (
      <p role="status" className="contact-form-status" style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink-3)' }}>
        Thanks — your message is on its way. I&apos;ll reply as soon as I can.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="contact-form" noValidate>
      {/* Honeypot — hidden from sighted users and screen readers, bots fill it anyway */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor={companyId}>Company</label>
        <input id={companyId} name="company" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="contact-form-field">
        <label htmlFor={nameId}>Name</label>
        <input id={nameId} name="name" type="text" required maxLength={100} autoComplete="name" />
      </div>

      <div className="contact-form-field">
        <label htmlFor={emailId}>Email</label>
        <input id={emailId} name="email" type="email" required maxLength={254} autoComplete="email" />
      </div>

      <div className="contact-form-field">
        <label htmlFor={messageId}>Message</label>
        <textarea id={messageId} name="message" required minLength={10} maxLength={5000} rows={5} />
      </div>

      <button type="submit" className="btn primary" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending…' : 'Send message'}
        <span className="arrow" aria-hidden="true">→</span>
      </button>

      {status === 'error' && (
        <p role="alert" className="contact-form-status contact-form-error" style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--accent)' }}>
          {errorMessage}
        </p>
      )}
    </form>
  );
}
